import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, ArrowLeft, Sparkles, HelpCircle } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useUI } from "../context/UIContext";
import { getArticle, ARTICLES } from "../data/articles";
import { IMAGES } from "../data/content";

export default function ArticlePage() {
  const { slug } = useParams();
  const a = getArticle(slug);
  const { openUpload } = useUI();
  if (!a) return <Navigate to="/ratgeber" replace />;

  const path = `/ratgeber/${a.slug}`;
  const related = ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3);
  const schema = [
    {
      "@context": "https://schema.org", "@type": "Article",
      headline: a.title, articleSection: a.cat, description: a.meta || a.excerpt,
      image: IMAGES[a.img],
      author: { "@type": "Person", name: "Kerstin Hemming" },
      publisher: { "@type": "Organization", name: "Energie-Tarifberater" },
      mainEntityOfPage: `https://www.energie-tarifberater.de${path}`,
    },
  ];
  if (a.faqs?.length) {
    schema.push({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: a.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
  }

  return (
    <>
      <Seo title={a.title} description={a.meta || a.excerpt} path={path} image={IMAGES[a.img]} schema={schema} />
      <PageHero eyebrow={`Ratgeber · ${a.cat}`} titleLines={[a.title]} subtitle={a.excerpt}
        image={IMAGES[a.img]} breadcrumb={[{ label: "Ratgeber", to: "/ratgeber" }, { label: a.cat }]} />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-8">
            <BookOpen className="h-3.5 w-3.5" /> {a.read} Lesezeit · von Kerstin Hemming
          </div>

          {a.intro && (
            <Reveal>
              <p className="text-xl sm:text-2xl font-heading font-semibold text-navy-900 leading-relaxed mb-10" data-testid="article-intro">{a.intro}</p>
            </Reveal>
          )}

          {a.takeaways?.length > 0 && (
            <Reveal>
              <div className="rounded-3xl border border-blue-100 bg-blue-50/60 p-7 mb-12" data-testid="article-takeaways">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-4 inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Das Wichtigste in Kürze</p>
                <ul className="space-y-2.5">
                  {a.takeaways.map((t, i) => (
                    <li key={i} className="flex gap-3 text-navy-900 text-sm leading-relaxed">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}

          <article className="space-y-10">
            {a.body.map((sec, i) => (
              <Reveal key={i} delay={Math.min(i * 0.04, 0.16)}>
                <h2 className="text-2xl font-heading text-navy-900 mb-3">{sec.h}</h2>
                <p className="text-slate-600 leading-relaxed text-lg font-light">{sec.p}</p>
              </Reveal>
            ))}
          </article>

          {a.faqs?.length > 0 && (
            <Reveal>
              <div className="mt-16" data-testid="article-faqs">
                <h2 className="text-2xl font-heading text-navy-900 mb-6 inline-flex items-center gap-2"><HelpCircle className="h-5 w-5 text-blue-700" /> Häufige Fragen</h2>
                <div className="space-y-4">
                  {a.faqs.map((f, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`article-faq-${i}`}>
                      <p className="font-heading text-navy-900 mb-2">{f.q}</p>
                      <p className="text-slate-500 text-sm leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <div className="mt-14 rounded-3xl bg-blue-700 p-10 text-center">
            <h3 className="text-2xl font-heading font-semibold text-white mb-3">Lieber direkt <span className="font-serif italic text-blue-200">prüfen lassen?</span></h3>
            <p className="text-blue-100 mb-6">Laden Sie Ihre Rechnung hoch – kostenlose Einschätzung in 24 Stunden.</p>
            <button onClick={openUpload} className="inline-flex items-center gap-2 rounded-full bg-white text-blue-800 px-8 py-4 font-semibold hover:bg-blue-50 transition-colors" data-testid="article-upload-btn">
              Rechnung kostenlos prüfen <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>

          <Link to="/ratgeber" className="mt-10 inline-flex items-center gap-2 text-blue-700 font-medium" data-testid="article-back-link"><ArrowLeft className="h-4 w-4" /> Alle Artikel</Link>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 mt-20">
          <h2 className="text-2xl font-heading text-navy-900 mb-6">Weitere Artikel</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((r) => (
              <Link key={r.slug} to={`/ratgeber/${r.slug}`} className="group rounded-3xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1.5 transition-transform duration-500" data-testid={`related-${r.slug}`}>
                <div className="h-40 overflow-hidden"><img src={IMAGES[r.img]} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-6">
                  <span className="text-xs text-blue-700 font-medium">{r.cat}</span>
                  <h3 className="font-heading text-navy-900 mt-1 group-hover:text-blue-700 transition-colors">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
